import { useCallback } from 'react';
import { trackSelectContent, trackSelectItem } from '@browsing/tracking';

import { ContinueShoppingCardItem } from '../types';
import { mapCardItemToTrackingItem } from '../helpers';
import { TRACKING_CONSTANTS } from '../constants';

interface UseTrackingReturn {
    trackArticleClick: (article: ContinueShoppingCardItem, index: number) => void;
    trackSeeAllClick: () => void;
    trackTabChange: (categoryId: string) => void;
    trackSwipe: () => void;
}

/**
 * Custom hook for handling all Continue Shopping tracking events
 * Centralizes tracking logic and reduces component complexity
 */
export const useTracking = (): UseTrackingReturn => {
    const trackArticleClick = useCallback((article: ContinueShoppingCardItem, index: number) => {
        trackSelectItem(
            {
                context: TRACKING_CONSTANTS.CONTEXT,
                element: TRACKING_CONSTANTS.ELEMENT,
                itemListName: TRACKING_CONSTANTS.ITEM_LIST_NAME,
                item: mapCardItemToTrackingItem(article),
            },
            {
                customMapper: () => ({ index }),
            },
        );
    }, []);

    const trackSeeAllClick = useCallback(() => {
        trackSelectContent(TRACKING_CONSTANTS.ELEMENT, 'click', {
            contentId: TRACKING_CONSTANTS.SEE_ALL_CONTENT_ID,
        });
    }, []);

    const trackTabChange = useCallback((categoryId: string) => {
        trackSelectContent(TRACKING_CONSTANTS.ELEMENT, 'click', {
            contentId: categoryId,
        });
    }, []);

    const trackSwipe = useCallback(() => {
        trackSelectContent(TRACKING_CONSTANTS.ELEMENT, 'swipe');
    }, []);

    return {
        trackArticleClick,
        trackSeeAllClick,
        trackTabChange,
        trackSwipe,
    };
};
